import { useState, useEffect, useCallback } from 'react';
import { useUpsertCarveOutMutation, useDeleteCarveOutMutation } from '../../../../../../../services/ContractService';
import { CarveOutView } from '../../../../../../../services/ContractService/types/contractTypes';

interface CarveOutFormData {
    code: string;
    description: string;
    rate: number;
    applyMultiProcedureDisc: boolean;
    isCaseRate: boolean;
}

interface UseCarveOutFormProps {
    editingCarveOut: CarveOutView | null;
    contractId: string;
    open: boolean;
    onClose: () => void;
}

interface UseCarveOutFormResult {
    formData: CarveOutFormData;
    formErrors: { [key: string]: string };
    isLoading: boolean;
    isDeleting: boolean;
    handleFormChange: (field: keyof CarveOutFormData, value: string | number | boolean) => void;
    handleSave: () => Promise<void>;
    handleDelete: () => Promise<void>;
}

const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const initialFormData: CarveOutFormData = {
    code: '',
    description: '',
    rate: 0,
    applyMultiProcedureDisc: false,
    isCaseRate: false,
};

export const useCarveOutForm = ({
    editingCarveOut,
    contractId,
    open,
    onClose
}: UseCarveOutFormProps): UseCarveOutFormResult => {
    const [formData, setFormData] = useState<CarveOutFormData>(initialFormData);
    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
    
    const [upsertCarveOut, { isLoading }] = useUpsertCarveOutMutation();
    const [deleteCarveOut, { isLoading: isDeleting }] = useDeleteCarveOutMutation();

    useEffect(() => {
        if (editingCarveOut) {
            setFormData({
                code: editingCarveOut.code,
                description: editingCarveOut.description,
                rate: editingCarveOut.rate,
                applyMultiProcedureDisc: editingCarveOut.applyMultiProcedureDisc,
                isCaseRate: editingCarveOut.isCaseRate,
            });
        } else {
            setFormData(initialFormData);
        }
        setFormErrors({});
    }, [editingCarveOut, open]);

    const handleFormChange = useCallback((field: keyof CarveOutFormData, value: string | number | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (formErrors[field]) {
            setFormErrors(prev => ({ ...prev, [field]: '' }));
        }
    }, [formErrors]);

    const validateForm = useCallback(() => {
        const errors: { [key: string]: string } = {};

        if (!formData.code.trim()) {
            errors.code = 'Code is required';
        }

        if (!formData.description.trim()) {
            errors.description = 'Description is required';
        }

        if (formData.rate <= 0) {
            errors.rate = 'Rate must be greater than 0';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formData]);

    const handleSave = useCallback(async () => {
        if (!validateForm()) return;

        try {
            const carveOutData: CarveOutView = {
                id: editingCarveOut?.id || generateTempId(),
                contractId,
                code: formData.code.trim(),
                description: formData.description.trim(),
                rate: formData.rate,
                applyMultiProcedureDisc: formData.applyMultiProcedureDisc,
                isCaseRate: formData.isCaseRate,
            };

            await upsertCarveOut({ carveOut: carveOutData }).unwrap();
            onClose();
        } catch (error) {
            console.error('Error saving CarveOut:', error);
        }
    }, [validateForm, editingCarveOut, contractId, formData, upsertCarveOut, onClose]);

    const handleDelete = useCallback(async () => {
        if (!editingCarveOut) return;

        const confirmDelete = window.confirm(`Are you sure you want to delete CarveOut "${editingCarveOut.code}"?`);
        if (!confirmDelete) return;

        try {
            await deleteCarveOut({
                carveOutId: editingCarveOut.id,
                contractId: editingCarveOut.contractId
            }).unwrap();
            onClose();
        } catch (error) {
            console.error('Error deleting CarveOut:', error);
        }
    }, [editingCarveOut, deleteCarveOut, onClose]);

    return {
        formData,
        formErrors,
        isLoading,
        isDeleting,
        handleFormChange,
        handleSave,
        handleDelete
    };
};
